import React, { useEffect, useState } from 'react';
import { makeStyles } from 'styles/util';
import { useDispatch } from 'react-redux';
import { handleResponse } from 'actions/actionHelpers';
import { fetchFaqs } from 'actions/faqs';
import faqCategories from 'enums/faqCategories';
import PropTypes from 'prop-types';

import Loader from 'common/Loader';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import ExpandableTitleDescriptionList from 'components/expandableTitleDescriptionList/ExpandableTitleDescriptionList';
import Typography from '@material-ui/core/Typography';
import MarginBox from 'common/MarginBox';

const useStyles = makeStyles(({ theme }) => ({
    faqCategoryList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        marginTop: theme.spacing(4)
    }
}));

const FaqSection = ({ filter = () => true, groupByCategory = true }) => {
    const classes = useStyles();
	const [faqs, setFaqs] = useState(undefined);
	const [isLoading, setIsLoading] = useState(true);
	const [expandedCategoryKeys, setExpandedCategoryKeys] = useState([]);
	const [populatedFaqCategories, setPopulatedFaqCategories] = useState([]);
	const dispatch = useDispatch();

	useEffect(() => {
        dispatch(fetchFaqs())
            .then(handleResponse(
                response => {
                    setFaqs(response.payload
                        .filter(filter)
                        .map(faq => ({
                            title: faq.question,
                            description: faq.answer,
                            category: faq.category
                        }))
                    );
                    setIsLoading(false);
                    setPopulatedFaqCategories(Object.values(faqCategories).filter(category => response.payload.some(faq => faq.category === category.key)));
                },
                () => {
                    setIsLoading(false);
                }
            ));
    }, []);

    const getFaqsForCategory = category => faqs
        .filter(faq => faq.category === category.key);

    const isExpanded = category => expandedCategoryKeys.includes(category.key);

    const handleButtonClick = category => {
        setExpandedCategoryKeys(
            populatedFaqCategories
                .map(c => c.key)
                .filter(key => key !== category.key && expandedCategoryKeys.includes(key))
                .concat(isExpanded(category) ? [] : category.key)
        );
    };

    if(isLoading) {
        return <Loader />;
    }

    if(!groupByCategory) {
        return (
            <Box className={classes.faqList}>
                <ExpandableTitleDescriptionList items={faqs}/>
            </Box>
        );
    }

    return (
        <Box className={classes.faqCategoryList}>
            {
                populatedFaqCategories
                    .map(category => (
                        <Box key={category.key}>
                            <Button
                                variant={isExpanded(category) ? 'contained' : 'outlined'}
                                color="primary"
                                fullWidth
                                onClick={() => handleButtonClick(category)}
                            >
                                <Typography variant="h4">{category.title}</Typography>
                            </Button>
                            {
                                isExpanded(category) && (
                                    <MarginBox bottom={4}>
                                        <ExpandableTitleDescriptionList items={getFaqsForCategory(category)}/>
                                    </MarginBox>
                                )
                            }
                        </Box>
                    ))
            }
        </Box>
    );
};

FaqSection.propTypes = {
    filter: PropTypes.func,
    groupByCategory: PropTypes.bool
};

export default FaqSection;

